import { useLazyQuery, useMutation, useQuery } from "@apollo/client/react";
import {
  Box,
  Button,
  Container,
  FormControl,
  IconButton,
  SvgIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
} from "@mui/material";
import MuiButton from "@mui/material/Button";
import { styled, useTheme } from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import { EditOutlined } from "@mui/icons-material";
import { DropDownList } from "@progress/kendo-react-dropdowns";
import { ReactComponent as arrowIcon } from "assets/icons/icon-arrow-blue.svg";
import certificationIcon from "assets/icons/icon-certification.svg";
import otherLearningIcon from "assets/icons/icon-course.svg";
import metricsIcon from "assets/icons/icon-metrics-temp.png";
import requiredIcon from "assets/icons/icon-required.svg";
import {
  Accordion,
  CounseleeAccordion,
  D3Chart,
  EmailDialog,
  MobileDrawer,
  Paginator,
  RemindDialog,
  SortableTableHeader,
  SubHeader,
  TabPanel,
  Tabs,
  theme,
  Toggle,
  Tooltip,
} from "components";
import { useBreadCrumbs, useLearningRecords } from "hooks";
import { openUrlUtil } from "hooks/open-url";
import { first, get, includes, pull, reduce, uniq } from "lodash";
import { ChangeEvent, Fragment, useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import { RootState, useAppDispatch } from "store";
import {
  LINE_MANAGER_EMAIL,
  PROFILE_FIELD_TYPE_NUMBER,
  PROFILE_FIELD_TYPE_SINGLE_LINE_TEXT,
  PROFILE_FIELD_TYPE_SINGLE_SELECT_DROPDOWN,
} from "utils/constants";
import * as CounseleeItemTypes from "utils/graphql/CoursesAndPathwaysByLearner";
import * as GlobalTypes from "utils/graphql/Global";
import { LearnerWorkshopActivitiesSortBy } from "utils/graphql/Global";
import * as LearnerProfileTypes from "utils/graphql/LearnerProfile";
import {
  LearnerProfile_learnerProfile_metaData_dataPoints,
  LearnerProfile_learnerProfile_metaData_dataPoints_items,
  LearnerProfile_learnerProfile_userProfile,
  LearnerProfile_learnerProfile_userProfile_dataPoints,
} from "utils/graphql/LearnerProfile";
import * as CounseleesTypes from "utils/graphql/LearnersByLineManager";
import * as LearnersMetricsTypes from "utils/graphql/LearnersMetrics";
import * as LearnerWorkshopsTypes from "utils/graphql/LearnerWorkshops";
import { LearnerWorkshops_learnerWorkshopActivities_activities } from "utils/graphql/LearnerWorkshops";
import * as ReportTypes from "utils/graphql/Reports";
import { emailRegex, formInputSanitize } from "utils/helpers";
import portalSettingsManager from "utils/portalSettingsManager";
import {
  EMAIL_UPDATE_STATUS,
  GET_COURSES_PATHWAYS_BY_LEARNER,
  GET_LEARNERS_BY_LINEMANAGER,
  GET_LEARNER_METRICS,
  GET_LEARNER_PROFILE,
  GET_MY_WORKSHOPS,
  GET_REPORTS,
  UPDATE_USER_PROFILE,
} from "utils/queries";
import "utils/string-prototype";
import {
  ILazyLoadableComponent,
  ProfileTabId,
  SortableTableHeaderType,
  TabModel,
} from "utils/types";
import { useFocusStyles } from "../../hooks/focusBorder";
import { MySkills } from "./ProfileSkills";

const useStyles = makeStyles(() => ({
  customHoverFocus: {
    "&:focus, &.Mui-focusVisible": { backgroundColor: "rgba(0, 0, 0, 0.04)" },
  },
  customColor: {
    color: "rgb(51, 51, 51) !important",
  },
  headingLabel: {
    marginBlockStart: "0",
    marginBlockEnd: "0",
    fontSize: "18px !important",
    color: "rgb(51, 51, 51) !important",
  },
  focusSelectItem: {
    zIndex: 10000,
    "& .k-list-container": {
      backgroundColor: "#ffffff",
    },
    "& .k-list-optionlabel, & .k-list-item": {
      backgroundColor: "#ffffff",
      borderRadius: "0px !important",
      padding: "6px",
      color: "black",
      fontSize: "14px !important",
      "&:hover": {
        backgroundColor: "#E6E6E6 !important",
        border: "2px solid #000000 !important",
      },
    },
  },
  selectStyle: {
    width: "100%",
    height: "32px",
    fontWeight: "500 !important",
    border: "none",
    borderRadius: "0px",
    backgroundColor: "#ffffff",
    "&::placeholder": {
      fontWeight: "500 !important",
      opacity: "0.6 !important",
    },
    "& .k-input-value-text": {
      fontSize: "14px !important",
      fontWeight: "500 !important",
    },
    "&.k-focus": {
      backgroundColor: "#ffffff",
    },
    "&:hover": {
      backgroundColor: "#ffffff",
    },
    "&::after": {
      border: "none !important",
    },
    "& .k-icon-button": {
      padding: "0px 20px !important",
    },
  },
  dl: {
    marginBlockStart: "0",
    marginBlockEnd: "0",
    margin: "20px 0",
    width: "100%",
    gridColumn: "1 / -1",
    display: "contents",
  },
  dt: {
    display: "block",
    fontSize: "12px",
    color: "rgb(51, 51, 51)",
    paddingBottom: "5px",
  },
  dd: {
    fontSize: "14px",
    color: theme.palette.grey["800"],
    fontWeight: 700,
    marginInlineStart: "0",
    marginBottom: "1rem",
  },
  ddEmail: {
    marginInlineStart: "0",
    maxWidth: "250px",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    cursor: "pointer",
    fontWeight: "bold",
    fontSize: "13px",
    color: "rgb(51, 51, 51) !important",
  },
}));

const Email = styled(Box)({
  maxWidth: "250px",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
  cursor: "pointer",
  fontWeight: "bold",
  fontSize: "13px",
});

const Clear = styled(Box)(({ theme }) => ({
  color: portalSettingsManager.application?.common?.themeColor,
  fontSize: "12px",
  fontWeight: 700,
  padding: "0px",
  float: "right",
  cursor: "pointer",
  display: "inline-block",
  margin: "6px 0 2px 0",
  lineHeight: "24px",
  minWidth: "44px",
  "&.outlined": {
    border: `1px solid ${portalSettingsManager.application?.common?.themeColor}`,
  },
  "&.disabled": {
    color: theme.palette.grey["500"],
  },
}));

const ProfileField = styled(Box)(({ theme }) => ({
  "& label": {
    display: "block",
    fontSize: "12px",
    color: "rgb(51, 51, 51)",
    paddingBottom: "5px",
  },
  "& label.with-clear": {
    display: "inline-block",
    marginTop: "12px",
  },
  "& span": {
    fontSize: "14px",
    color: theme.palette.grey["800"],
    fontWeight: 700,
  },
  "& .MuiInput-underline": {
    width: "100%",
    borderBottom: "0px",
    "&:before": {
      borderBottom: "0px !important",
    },
    "&:after": {
      borderBottom: "0px",
    },
    "& .MuiSelect-icon": {
      right: "10px",
    },
  },
  "& select": {
    color: theme.palette.primary.main,
    fontSize: "12px",
    lineHeight: "30px",
  },
  "& button": {
    border: `1px solid ${theme.palette.primary.main}`,
    color: theme.palette.primary.main,
    position: "relative",
    left: "15px",
    top: "-2px",
    "&:focus": {
      outline: "none",
      border: "2px solid #000000",
    },
  },
  "& button span": {
    color: theme.palette.primary.main,
    fontSize: 12,
  },
  "& p": {
    fontSize: "11px",
    color: theme.palette.grey["500"],
  },
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
  width: "19rem",
}));

const SaveButton = styled(MuiButton)(({ theme }) => ({
  flex: 1,
  borderRadius: 0,
  fontSize: 11,
  fontWeight: 700,
  "&.MuiButton-outlined": {
    border: `1px solid ${theme.palette.grey["A100"]}`,
  },
  "&.Mui-disabled": {
    color: theme.palette.grey["500"],
  },
}));

const SectionCol2 = styled("section")(({ isMobile }: { isMobile?: boolean }) => {
  return {
    display: "grid",
    gridTemplateColumns: `repeat(${isMobile ? 1 : 2}, minmax(0, 1fr))`,
    gap: "1rem",
    marginTop: isMobile ? "20px" : undefined,
  };
});
SectionCol2.displayName = "SectionCol2";

const SectionCol3 = styled("section")(({ isMobile }: { isMobile?: boolean }) => {
  return {
    display: "grid",
    gridTemplateColumns: `repeat(${isMobile ? 1 : 2}, minmax(0, 1fr))`,
    columnGap: "1rem",
    margin: "20px 0",
    width: "100%",
  };
});
SectionCol3.displayName = "SectionCol3";

const CtlWrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile?: boolean }>(({ theme, isMobile }) => ({
  display: "flex",
  alignItems: "center",
  width: isMobile ? "100%" : "19rem",
  minHeight: "40px",
  marginTop: "5px",
  padding: "0 12px 0 0",
  overflow: "visible",
  "& input": {
    color: theme.palette.primary.main,
    border: `1px solid ${theme.palette.grey["A100"]}`,
    width: "100%",
    padding: "10px",
    height: "12px",
    "&:focus": {
      outline: "none",
      border: "2px solid #000000",
    },
  },
  "& select": {
    color: theme.palette.common.black,
    border: `1px solid ${theme.palette.grey["A100"]}`,
    padding: "10px",
    width: "100%",
    fontSize: "12px",
    lineHeight: "30px",
    "&:focus": {
      outline: "none",
      border: "2px solid #000000",
    },
  },
}));

const Legend = styled(Typography, {
  shouldForwardProp: (prop) => prop !== "color",
})<{ color?: string; component?: React.ElementType }>(({ theme, color }) => ({
  fontSize: "12px",
  color: theme.palette.grey["500"],
  margin: "10px",
  "&:before": {
    content: '""',
    display: "inline-block",
    backgroundColor: color || portalSettingsManager.statusColors?.enrolled?.back || "#c6007e",
    width: "10px",
    height: "10px",
    borderRadius: "5px",
    marginRight: "10px",
  },
}));

const CustomFormControl = styled(FormControl, {
  shouldForwardProp: (prop) => prop !== "isMobile",
})<{ isMobile?: boolean }>(({ isMobile }) => ({
  width: isMobile ? "100%" : "19rem",
  border: "1px solid black",
  padding: "2px",
  "&:focus-within": {
    border: "2px solid #000000",
  },
}));

const MetricsContainer: React.FC<{
  mobile: boolean;
  showMetrics: boolean;
  setShowMetrics: (show: boolean) => unknown;
  children: React.ReactNode;
}> = ({ mobile, showMetrics, setShowMetrics, children }) => {
  const { t } = useTranslation();
  return mobile ? (
    <MobileDrawer
      closeMessage={t("close_metrics")}
      isOpen={showMetrics}
      onVisibilityChange={setShowMetrics}
    >
      {children}
    </MobileDrawer>
  ) : (
    <Box>{children}</Box>
  );
};

export interface Autoenrolled_topics {
  id: string | null;
  name: string | null;
}

export const Profile = (): React.ReactElement => {
  const theme = useTheme();
  const { t } = useTranslation();
  const classes = useStyles();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const showMetricsContainer = useMediaQuery(theme.breakpoints.down(1050));
  const userContext = useSelector((state: RootState) => state.core.userContext);
  const dispatch = useAppDispatch();
  const [isEmailUpdateInProgress, setIsEmailUpdateInProgress] = useState<boolean | null>(true);
  const [checkStatus, setCheckStatus] = useState<boolean | null>(true);
  const [sendReminder, setSendReminder] = useState<boolean>(false);
  const [learnerName, setLearnerName] = useState<string>("");
  const [remindData, setRemindData] = useState<any>(null);
  const [tabs, setTabs] = useState<TabModel<number>[]>([]);
  const [urlParam, setUrlParam] = useState<string>(null);
  const labelPersonalDetails = `Personal Details`;
  const labelProfessionalDetails = `Professional Details`;
  const {
    getresult,
    result,
    sendReminder: handleSendReminder,
  } = useLearningRecords(
    remindData?.learnerId,
    remindData?.id,
    remindData?.contentType,
    remindData?.name,
    remindData?.dueDate,
  );
  const [expanded, setExpanded] = useState("");
  const [editEmail, setEditEmail] = useState<boolean>(false);
  const [initials, setInitials] = useState("");
  const [profile, setProfile] = useState<
    LearnerProfile_learnerProfile_userProfile | null | undefined
  >(null);
  const [topics, setTopics] = useState<
    LearnerProfileTypes.LearnerProfile_learnerProfile_metaData_topics[]
  >([]);
  const [editablePersonalConfig, setEditablePersonalConfig] = useState<
    LearnerProfile_learnerProfile_metaData_dataPoints[]
  >([]);
  const [readonlyPersonalConfig, setReadonlyPersonalConfig] = useState<
    LearnerProfile_learnerProfile_metaData_dataPoints[]
  >([]);
  const [readonlyProfessionalConfig, setReadonlyProfessionalConfig] = useState<
    LearnerProfile_learnerProfile_metaData_dataPoints[]
  >([]);
  const [editableProfessionalConfig, setEditableProfessionalConfig] = useState<
    LearnerProfile_learnerProfile_metaData_dataPoints[]
  >([]);
  const [formValues, setFormValues] = useState<Record<string, string | null> | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [autoEnrolledTopics, setAutoEnrolledTopics] = useState<Autoenrolled_topics[]>([]);
  const [dirtyTabIndices, setDirtyTabIndices] = useState<ProfileTabId[]>([]);
  const [currentTabIndex, setCurrentTabIndex] = useState<number>(0);
  const [certification, setCertification] = useState<any[]>([]);
  const [requiredLearning, setRequiredLearning] = useState<any[]>([]);
  const [otherLearnings, setOtherLearnings] = useState<any[]>([]);
  const [calendarLoading, setCalendarLoading] = useState<boolean>();
  const [mySkillsLoading, setMySkillsLoading] = useState<boolean>();

  const {
    showPersonalDetails,
    showProfessionalDetails,
    showLearnerMetricsOnProfilePage,
    canChangeEmailAddress,
    hasProfileReport: showProfileReport,
    hasSkillAssessment: showSkillAssessment,
    enableMyCalendar,
    enableIBFFlow,
  } = portalSettingsManager?.application?.features;

  const {
    data: profileData,
    loading: profileLoading,
    refetch: profileRefetch,
  } = useQuery<LearnerProfileTypes.LearnerProfile>(GET_LEARNER_PROFILE, {
    skip: !showPersonalDetails && !showProfessionalDetails,
    fetchPolicy: "no-cache",
  });

  const { data: emailUpdateInProgressData, refetch: emailUpdateInProgressRefetch } = useQuery<any>(
    EMAIL_UPDATE_STATUS,
    {
      skip: !showPersonalDetails,
      fetchPolicy: "no-cache",
    },
  );

  const { data: metricsList, loading: metricsLoading } =
    useQuery<LearnersMetricsTypes.LearnersMetrics>(GET_LEARNER_METRICS, {
      fetchPolicy: "no-cache",
    });

  // state for mobile metrics view
  const [showMetrics, setShowMetrics] = useState(false);

  const [getCounseleeItemDetail, { data: counseleeItems, loading: counseleeItemsLoading }] =
    useLazyQuery<CounseleeItemTypes.CoursesAndPathwaysByLearnerVariables>(
      GET_COURSES_PATHWAYS_BY_LEARNER,
    );

  const { data: counseleesList, loading: counseleeListLoading } =
    useQuery<CounseleesTypes.LearnersByLineManager>(GET_LEARNERS_BY_LINEMANAGER, {
      skip: !userContext?.userContext?.isLineManager,
    });

  const [updateProfile] = useMutation(UPDATE_USER_PROFILE);

  const [learnerDiagnostics, setlearnerDiagnostics] = useState<ReportTypes.Reports>();

  const [
    getLearnerDiagonistics,
    {
      data: learnerDiagnosticsData,
      loading: learnerDiagnosticsLoading,
      error: learnerDiagnosticsError,
    },
  ] = useLazyQuery<ReportTypes.Reports>(GET_REPORTS);

  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();

  const generateTabData = useCallback(
    (profileTabId: ProfileTabId): TabModel<number> => {
      const title = t(profileTabId);

      return {
        tabId: profileTabId,
        tabPath: title.replaceAll(" ", "").toLowerCase(),
        label: title.toTitleCase(),
        isDirty: includes(dirtyTabIndices, profileTabId),
      };
    },
    [t, dirtyTabIndices],
  );

  useEffect(() => {
    const activeTabPath = get(
      params,
      "activeTabId",
      generateTabData(ProfileTabId.ProfileDetails).tabPath,
    );
    setUrlParam(activeTabPath);
  }, [generateTabData, params, setUrlParam]);

  useEffect(() => {
    const newTabs = [];
    if (showProfessionalDetails || showPersonalDetails) {
      newTabs.push(generateTabData(ProfileTabId.ProfileDetails));
    }

    if (userContext?.userContext?.showTopicsOfInterest && !enableIBFFlow) {
      newTabs.push(generateTabData(ProfileTabId.TopicOfInterest));
    }

    if (userContext?.userContext?.isLineManager) {
      newTabs.push(generateTabData(ProfileTabId.MyTeam));
    }

    if (showProfileReport) {
      newTabs.push(generateTabData(ProfileTabId.ProfileReport));
    }

    if (enableMyCalendar) {
      newTabs.push(generateTabData(ProfileTabId.MyCalendar));
    }

    if (showSkillAssessment) {
      newTabs.push(generateTabData(ProfileTabId.MySkills));
    }

    setTabs(newTabs);
  }, [
    userContext,
    showProfessionalDetails,
    showPersonalDetails,
    showProfileReport,
    showSkillAssessment,
    generateTabData,
  ]);

  useEffect(() => {
    const newTabIndex = tabs.findIndex((t) => t.tabPath == urlParam);
    setCurrentTabIndex(newTabIndex == -1 ? 0 : newTabIndex);
  }, [urlParam, tabs, setCurrentTabIndex]);

  useEffect(() => {
    dispatch({
      type: "loader/showandhide",
      payload: {
        show:
          counseleeItemsLoading ||
          counseleeListLoading ||
          metricsLoading ||
          learnerDiagnosticsLoading,
        message: t("profile_loading"),
      },
    });
  }, [
    counseleeItemsLoading,
    counseleeListLoading,
    metricsLoading,
    learnerDiagnosticsLoading,
    dispatch,
    t,
  ]);

  const [learnerReportsDataFailed, setLearnerReportsDataFailed] = useState(false);

  useEffect(() => {
    if (showProfileReport) {
      getLearnerDiagonistics();
    }
  }, [showProfileReport, getLearnerDiagonistics]);

  useEffect(() => {
    if (!learnerDiagnosticsLoading) {
      setlearnerDiagnostics(learnerDiagnosticsData);
    }
    if (learnerDiagnosticsError) {
      setLearnerReportsDataFailed(true);
    }
  }, [
    learnerDiagnosticsData,
    learnerDiagnosticsLoading,
    learnerDiagnosticsError,
    setlearnerDiagnostics,
  ]);

  useEffect(() => {
    if (emailUpdateInProgressData) {
      setIsEmailUpdateInProgress(
        emailUpdateInProgressData?.isUpdateEmailRequestPending ? true : false,
      );
    }
  }, [emailUpdateInProgressData, checkStatus, setIsEmailUpdateInProgress]);

  useEffect(() => {
    const colors = [
      { color: portalSettingsManager.statusColors?.enrolled?.back || "#C6007E", type: "enrolled" },
      {
        color: portalSettingsManager.statusColors?.inprogress?.back || "#005EB8",
        type: "inProgress",
      },
      { color: portalSettingsManager.statusColors?.completed?.back || "#16837D", type: "complete" },
    ];

    if (metricsList) {
      const learnerMetrics = metricsList.learnersMetrics || {};
      const certObject = formatMetricsObject(get(learnerMetrics, "certifications", {}));
      setCertification(certObject.map((item, i) => Object.assign({}, item, colors[i])));
      const reqLearningObject = formatMetricsObject(get(learnerMetrics, "requiredLearning", {}));
      setRequiredLearning(reqLearningObject.map((item, i) => Object.assign({}, item, colors[i])));
      const otherLearningObj = formatMetricsObject(get(learnerMetrics, "otherLearnings", {}));
      setOtherLearnings(otherLearningObj.map((item, i) => Object.assign({}, item, colors[i])));
    }
  }, [metricsList]);

  const { handleBreadCrumb, breadCrumbData, handleBreadCrumbNavigation } = useBreadCrumbs();
  useEffect(() => {
    if (breadCrumbData && profileData) {
      handleBreadCrumb({ title: t("profile"), path: "/profile", type: "profile" }, "profile", "");
    }
  }, [profileData, breadCrumbData, handleBreadCrumb, t]);

  const formatMetricsObject = (data: any) => {
    const metricsArray = [];
    for (const [key, value] of Object.entries(data)) {
      if (key !== "total" && key !== "__typename") {
        const obj = {
          value: value,
          text: value,
          type: key,
        };
        metricsArray.push(obj);
      }
    }
    return metricsArray;
  };

  const handleTabChange = (__: ChangeEvent<unknown>, newValue: number) => {
    const oldTab = tabs[currentTabIndex];
    const newTab = tabs[newValue];

    const path = oldTab ? location.pathname.replace(`/${oldTab.tabPath}`, "") : location.pathname;

    navigate(`${path}/${newTab.tabPath}`);
  };

  const toggleTopic = (code: string) => {
    const originalLength = selectedTopics.length;
    pull(selectedTopics, code);

    if (selectedTopics.length === originalLength) {
      selectedTopics.push(code);
    }

    setSelectedTopics(uniq(selectedTopics).sort());
    setDirtyTabIndices(uniq([...dirtyTabIndices, ProfileTabId.TopicOfInterest]));
  };

  const updateProfileValues = (gatewayCode: string, value: string | null) => {
    setFormValues({
      ...formValues,
      [formInputSanitize(gatewayCode)]: formInputSanitize(value),
    });
    setDirtyTabIndices(uniq([...dirtyTabIndices, ProfileTabId.ProfileDetails]));
  };

  const createInput = (item: LearnerProfile_learnerProfile_metaData_dataPoints) => {
    const cleanOptions = (item.items || []).flatMap((option) => (option ? [option] : []));
    let value = get(formValues, item.code || "", null);
    const gatewayCode = item.code || "";

    const cleanLabel = t(item.code || "");
    const htmlLabel = `${cleanLabel.replaceAll(" ", "-")}-id`;
    const selectBoxId = `${cleanLabel.replaceAll(/[\s?]/g, "-")}-choose-option-select-box`;

    if (!item.canEdit) {
      if (
        item.type === PROFILE_FIELD_TYPE_SINGLE_SELECT_DROPDOWN &&
        cleanOptions &&
        cleanOptions.length > 0
      ) {
        const opt: LearnerProfile_learnerProfile_metaData_dataPoints_items | undefined =
          cleanOptions.find((o) => o.code === value);
        value = opt?.name || "";
      }
      return (
        <ProfileField key={item.code}>
          <label htmlFor={htmlLabel} style={{ paddingBottom: "5px", marginTop: "6px" }}>
            {cleanLabel}
          </label>
          <Tooltip position="right" title={value ? value : ""}>
            <Email id={htmlLabel} className={classes.customColor}>
              {value}
            </Email>
          </Tooltip>
        </ProfileField>
      );
    }

    const tabIndex = item.code === LINE_MANAGER_EMAIL ? -1 : 0;

    switch (item.type) {
      case PROFILE_FIELD_TYPE_SINGLE_LINE_TEXT:
        return (
          <ProfileField key={item.code}>
            <label tabIndex={tabIndex} htmlFor={htmlLabel} style={{ marginTop: "6px" }}>
              {cleanLabel}
            </label>
            <CtlWrapper isMobile>
              <input
                id={htmlLabel}
                value={value || ""}
                onChange={(event) => updateProfileValues(gatewayCode, event.target.value)}
              />
            </CtlWrapper>
          </ProfileField>
        );
      case PROFILE_FIELD_TYPE_NUMBER:
        return (
          <ProfileField key={item.code}>
            <label htmlFor={htmlLabel}>{cleanLabel}</label>
            <CtlWrapper isMobile>
              <input
                id={htmlLabel}
                type="number"
                value={value || ""}
                onChange={(event) => updateProfileValues(gatewayCode, event.target.value)}
              />
            </CtlWrapper>
          </ProfileField>
        );
      case PROFILE_FIELD_TYPE_SINGLE_SELECT_DROPDOWN:
        const selectedValue = cleanOptions.find((opt) => opt.code === value);
        const defaultValue = { name: t("choose"), code: null };
        return (
          <ProfileField key={item.code}>
            <div>
              <label id={htmlLabel} htmlFor={`${selectBoxId}`} className="with-clear">
                {cleanLabel}
              </label>
              <Clear
                onClick={() => {
                  value === null || value === "" ? false : updateProfileValues(gatewayCode, null);
                }}
                className={classes.focusSelectItem}
                tabIndex={0}
                role="button"
                aria-label={t("clear").toLowerCase()}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    if (value !== null && value !== "") {
                      updateProfileValues(gatewayCode, null);
                    }
                  }
                }}
              >
                {t("clear").toUpperCase()}
              </Clear>
            </div>
            <CustomFormControl isMobile>
              <DropDownList
                id={selectBoxId}
                style={{ width: "auto" }}
                ariaLabelledBy={htmlLabel}
                data={cleanOptions}
                textField="name"
                popupSettings={{ className: classes.focusSelectItem }}
                dataItemKey="code"
                className={classes.selectStyle}
                defaultItem={defaultValue}
                value={selectedValue ?? defaultValue}
                onChange={(event: any) => updateProfileValues(gatewayCode, event.target.value.code)}
                title={selectedValue ? selectedValue.name : defaultValue.name}
              />
            </CustomFormControl>
          </ProfileField>
        );
    }
  };

  const handleEditEmailClose = (updated: boolean) => {
    if (updated) {
      setIsEmailUpdateInProgress(true);
      setCheckStatus(!checkStatus);
      emailUpdateInProgressRefetch();
    }
    setEditEmail(false);
  };

  const handleSaveProfile = () => {
    dispatch({ type: "loader/showandhide", payload: { show: true } });
    const profileInfo = reduce(
      formValues,
      (result, value, key) => {
        result?.push({ code: key, value });
        return result;
      },
      [] as GlobalTypes.UserProfileInput[],
    );
    const subscribedChannels = selectedTopics.filter((value) => {
      return !(profile?.topicOfInterest?.findIndex((i) => i.id === value) >= 0);
    });
    const unSubscribedChannels = (profileData.learnerProfile?.userProfile?.topicOfInterest || [])
      .flatMap((topic) => (topic ? [topic.id] : []))
      .filter((value) => {
        return !selectedTopics.includes(value ? value : "");
      });
    const postData = {
      userProfileInput: includes(dirtyTabIndices, ProfileTabId.ProfileDetails) ? profileInfo : [],
      subscribedChannelIds: subscribedChannels,
      unsubscribedChannelIds: unSubscribedChannels,
    };
    updateProfile({ variables: { profile: postData } })
      .then(() => {
        setDirtyTabIndices([]);
        profileRefetch();
        dispatch({ type: "loader/showandhide", payload: { show: false } });
        dispatch({
          type: "alert/show",
          payload: {
            type: "success",
            title: t("profile_updating_dialogue_title"),
            message: t("profile_updating_success_message"),
          },
        });
      })
      .catch(() => {
        dispatch({ type: "loader/showandhide", payload: { show: false } });
        dispatch({
          type: "alert/show",
          payload: {
            type: "error",
            title: t("profile_updating_dialogue_error_title"),
            message: t("profile_updating_error_message"),
          },
        });
      });
  };

  useEffect(() => {
    return function cleanup() {
      dispatch({ type: "navigation/unblock" });
    };
  }, [dispatch]);

  useEffect(() => {
    const PERSONAL_FIELD_NAMES = ["LineManagerEmail", "AccessibilityRequirement"];
    if (profileData) {
      const config = (profileData.learnerProfile?.metaData?.dataPoints || []).flatMap((item) =>
        item ? [item] : [],
      );
      const personalConfig = config.filter((item) => includes(PERSONAL_FIELD_NAMES, item.code));
      const professionalConfig = config.filter(
        (item) =>
          !includes(PERSONAL_FIELD_NAMES, item.code) &&
          item.code !== "LineManagerFirstName" &&
          item.code !== "LineManagerLastName",
      );

      setProfile(profileData.learnerProfile?.userProfile);
      setEditablePersonalConfig(personalConfig.filter((item) => item.canEdit));
      setReadonlyPersonalConfig(personalConfig.filter((item) => !item.canEdit));
      setEditableProfessionalConfig(professionalConfig.filter((item) => item.canEdit));
      setReadonlyProfessionalConfig(professionalConfig.filter((item) => !item.canEdit));

      setTopics(
        (profileData.learnerProfile?.metaData?.topics || []).flatMap((topic) =>
          topic ? [topic] : [],
        ),
      );
      setSelectedTopics(
        (profileData.learnerProfile?.userProfile?.topicOfInterest || []).flatMap((topic) =>
          topic ? [topic.id] : [],
        ),
      );
      setAutoEnrolledTopics(
        (profileData.learnerProfile?.userProfile?.topicOfInterest || []).flatMap((topic) =>
          topic && topic?.isAutoEnrolled
            ? profileData.learnerProfile?.metaData?.topics.flatMap((x) =>
                x.id == topic.id ? x : [],
              )
            : [],
        ),
      );
    }
  }, [profileData]);

  useEffect(() => {
    const dataPoints = (profile?.dataPoints || []).flatMap((item) => (item ? [item] : []));

    setInitials(
      `${first(userContext?.userContext?.firstName || "")}${first(
        userContext?.userContext?.lastName || "",
      )}`,
    );

    setFormValues(
      reduce(
        dataPoints,
        (
          result: { [key: string]: any },
          item: LearnerProfile_learnerProfile_userProfile_dataPoints,
        ) => {
          if (item.code) {
            result[item.code] = item.value || item.values;
          }

          return result;
        },
        {},
      ),
    );
  }, [profile]);

  useEffect(() => {
    if (dirtyTabIndices.length) {
      dispatch({
        type: "navigation/block",
        payload: t("profile_navigation_warning"),
      });
    } else {
      dispatch({ type: "navigation/unblock" });
    }
  }, [dirtyTabIndices, dispatch, t]);

  const clearChoiceClick = () => {
    setSelectedTopics([]);
    setDirtyTabIndices(uniq([...dirtyTabIndices, ProfileTabId.TopicOfInterest]));
  };

  const validateEmail = () => {
    if (formValues && formValues?.LineManagerEmail && formValues?.LineManagerEmail.length > 0) {
      const userEmail = userContext?.userContext?.learnerEmail;
      if (
        userEmail &&
        userEmail.toLowerCase().trim() === formValues?.LineManagerEmail.toLowerCase().trim()
      ) {
        return true;
      }
      return !new RegExp(emailRegex).test(formValues?.LineManagerEmail.trim() || "");
    }
  };
  const handleBreadCrumbClick = (path: string, breadCrumbKey: any) => {
    handleBreadCrumbNavigation(path, breadCrumbKey);
  };
  const visibleReminder = (params: any, learnerId: string) => {
    getresult();
    setSendReminder(true);
    setRemindData({ ...params?.dataItem, learnerId });
  };

  const getTabIndexById = (profileTabType: ProfileTabId) => {
    return tabs.findIndex((t) => t.tabId == profileTabType);
  };

  const tabPanelSettings = { persistInDom: true };

  const focusClass = useFocusStyles();
  const refreshReportDiagnositicData = () => {
    getLearnerDiagonistics();
  };

  useEffect(() => {
    if (editableProfessionalConfig) {
      const dropdownList = document.querySelectorAll(".k-dropdown-wrap");
      for (let index = 0; index < dropdownList.length; index++) {
        dropdownList[index].children[0].setAttribute("aria-hidden", "true");
        dropdownList[index].children[1].setAttribute("aria-hidden", "true");
        dropdownList[index].setAttribute("role", "combobox");
        dropdownList[index].setAttribute("aria-haspopup", "listbox");
      }
    }
  }, [editableProfessionalConfig]);

  return (
    <>
      <SubHeader
        contentType="Profile"
        breadCrumbData={breadCrumbData}
        handleBreadCrumbClick={handleBreadCrumbClick}
      />
      <Container style={{ padding: isMobile ? "12px 20px 16px 20px" : undefined }}>
        <Box display="flex" flexDirection="column">
          <Box
            display={showMetricsContainer ? "flex" : "none"}
            alignItems="center"
            justifyContent="space-between"
            marginTop="12px"
            marginBottom="12px"
          >
            <Typography
              style={{
                fontSize: "12px",
                fontWeight: 700,
                letterSpacing: "2px",
              }}
            >
              {t("profile_upper")}
            </Typography>
            {showLearnerMetricsOnProfilePage && (
              <Box width="46px" height="46px" onClick={() => setShowMetrics(true)}>
                <img
                  style={{ width: "100%", height: "100%", cursor: "pointer" }}
                  src={metricsIcon}
                  alt="Metrics"
                />
              </Box>
            )}
          </Box>
          <Box display="flex">
            <Box
              width="50px"
              height="50px"
              marginRight="20px"
              display="flex"
              justifyContent="center"
              alignItems="center"
              style={{ background: theme.palette.grey["800"], borderRadius: "25px" }}
            >
              <Typography
                style={{
                  fontSize: "20px",
                  fontWeight: 700,
                  color: theme.palette.common.white,
                  letterSpacing: "2px",
                }}
              >
                {initials}
              </Typography>
            </Box>
            <Box marginBottom="35px">
              <Typography
                display="block"
                style={{ fontSize: "30px", color: theme.palette.grey["800"] }}
                component="h2"
              >
                {userContext?.userContext?.userDisplayName}
              </Typography>
              <Typography
                display="block"
                style={{ fontSize: "12px", color: theme.palette.grey["800"] }}
              >
                {profile?.designation}
                {profile?.designation && profile?.department && ", "}
                {profile?.department}
              </Typography>
            </Box>
          </Box>
          <Box flex="1">
            <Box display={isMobile ? "block" : isSmallScreen ? "flex" : "flex"}>
              <Box flex="1" position="relative">
                <Typography
                  style={{
                    fontSize: "18px",
                    fontWeight: 700,
                    color: theme.palette.grey["800"],
                    lineHeight: "40px",
                  }}
                  display={isMobile ? "inline" : "block"}
                  component="h3"
                >
                  {t("profile_overview")}
                </Typography>
                {dirtyTabIndices.length > 0 && (
                  <SaveButton
                    variant="outlined"
                    onClick={handleSaveProfile}
                    style={{
                      position: isSmallScreen ? "fixed" : isMobile ? "static" : "absolute",
                      marginLeft: isSmallScreen ? "0px" : isMobile ? "20px" : undefined,
                      right: "0px",
                      top: isSmallScreen ? undefined : "60px",
                      borderRadius: 0,
                      fontSize: "11px",
                      fontWeight: 700,
                      padding: "10px 30px",
                      letterSpacing: 0,
                      zIndex: 1,
                      color: isSmallScreen
                        ? "#fff"
                        : portalSettingsManager.buttonColors?.normal?.active?.text ||
                          theme.palette.grey["800"],
                      backgroundColor: isSmallScreen
                        ? "#0091da"
                        : portalSettingsManager.buttonColors?.normal?.active?.back,
                      border: isSmallScreen
                        ? "1px solid #0091da"
                        : `1px solid ${
                            portalSettingsManager.buttonColors?.normal?.active?.border ||
                            theme.palette.grey["A100"]
                          }`,
                      width: isSmallScreen ? "100%" : undefined,
                      bottom: isSmallScreen ? "0px" : undefined,
                    }}
                    className={`${focusClass.focusItem} ${focusClass.secondaryHover}`}
                    disabled={!dirtyTabIndices.length || validateEmail()}
                  >
                    {t("save_changes")}
                  </SaveButton>
                )}
                {tabs && tabs.length > 0 && (
                  <Fragment>
                    <Tabs
                      value={currentTabIndex}
                      mobile={isMobile}
                      tabs={tabs}
                      onChange={handleTabChange}
                    />
                    {(showPersonalDetails || showProfessionalDetails) && (
                      <TabPanel
                        {...tabPanelSettings}
                        loading={profileLoading}
                        value={currentTabIndex}
                        index={getTabIndexById(ProfileTabId.ProfileDetails)}
                        style={{ padding: "24px 0" }}
                      >
                        {showPersonalDetails && (
                          <Accordion
                            title={
                              <Typography
                                style={{
                                  fontSize: "18px",
                                  fontWeight: 700,
                                  color: theme.palette.grey["800"],
                                }}
                                component="h4"
                              >
                                {t("personal_details")}
                              </Typography>
                            }
                            outlined
                            mobile={isMobile}
                            buttonText={labelPersonalDetails}
                          >
                            <SectionCol3 isMobile={isMobile}>
                              <dl className={classes.dl}>
                                <>
                                  <dt className={classes.dt} style={{ gridColumn: 1, gridRow: 1 }}>
                                    {t("my_first_name")}
                                  </dt>
                                  <dd className={classes.dd} style={{ gridColumn: 1, gridRow: 2 }}>
                                    {profile?.firstName}
                                  </dd>
                                </>
                                <>
                                  <dt
                                    className={classes.dt}
                                    style={{
                                      gridColumn: isMobile ? 1 : 2,
                                      gridRow: isMobile ? 3 : 1,
                                    }}
                                  >
                                    {t("my_last_name")}
                                  </dt>
                                  <dd
                                    className={classes.dd}
                                    style={{
                                      gridColumn: isMobile ? 1 : 2,
                                      gridRow: isMobile ? 4 : 2,
                                    }}
                                  >
                                    {profile?.lastName}
                                  </dd>
                                </>
                                <>
                                  <dt
                                    className={classes.dt}
                                    style={{ gridColumn: 1, gridRow: isMobile ? 5 : 3 }}
                                  >
                                    {t("my_email")}
                                  </dt>
                                  {canChangeEmailAddress && isEmailUpdateInProgress == false && (
                                    <span className={classes.customColor}>
                                      <IconButton
                                        aria-label={t("edit_email")}
                                        tabIndex={0}
                                        onClick={() => {
                                          setCheckStatus(!checkStatus);
                                          setEditEmail(true);
                                        }}
                                        size="small"
                                        style={{
                                          borderColor:
                                            portalSettingsManager.application?.common?.themeColor ||
                                            theme.palette.primary.main,
                                        }}
                                      >
                                        <EditOutlined
                                          className={classes.customHoverFocus}
                                          fontSize="inherit"
                                          style={{
                                            color:
                                              portalSettingsManager.application?.common
                                                ?.themeColor || theme.palette.primary.main,
                                          }}
                                        />
                                      </IconButton>
                                    </span>
                                  )}
                                  <dd
                                    className={classes.ddEmail}
                                    style={{ gridColumn: 1, gridRow: isMobile ? 6 : 4 }}
                                  >
                                    <Tooltip
                                      title={profile ? profile.email : ""}
                                      aria-label={profile ? profile.email : ""}
                                    >
                                      <span tabIndex={0} aria-describedby="email-tooltip">
                                        {profile?.email}
                                      </span>
                                    </Tooltip>
                                  </dd>
                                </>
                                {formValues &&
                                  readonlyPersonalConfig.map((item) => createInput(item))}
                              </dl>
                            </SectionCol3>
                            <SectionCol2 isMobile={isMobile}>
                              {formValues &&
                                editablePersonalConfig.map((item) => createInput(item))}
                            </SectionCol2>
                          </Accordion>
                        )}
                        {showPersonalDetails && showProfessionalDetails && (
                          <Box
                            style={{
                              margin: "40px 0",
                              borderTop: `1px solid ${theme.palette.grey["300"]}`,
                            }}
                          />
                        )}
                        {showProfessionalDetails && (
                          <Accordion
                            title={
                              <Typography
                                style={{
                                  fontSize: "18px",
                                  fontWeight: 700,
                                  color: theme.palette.grey["800"],
                                }}
                                component="h4"
                              >
                                {t("professional_details")}
                              </Typography>
                            }
                            outlined
                            mobile={isMobile}
                            buttonText={labelProfessionalDetails}
                          >
                            <SectionCol2 isMobile={isMobile}>
                              {formValues &&
                                readonlyProfessionalConfig.map((item) => createInput(item))}
                              {formValues &&
                                editableProfessionalConfig.map((item) => createInput(item))}
                            </SectionCol2>
                          </Accordion>
                        )}
                      </TabPanel>
                    )}
                    {userContext?.userContext?.showTopicsOfInterest && !enableIBFFlow && (
                      <TabPanel
                        {...tabPanelSettings}
                        loading={profileLoading}
                        value={currentTabIndex}
                        index={getTabIndexById(ProfileTabId.TopicOfInterest)}
                        style={{ padding: "40px 0px 15px 0px" }}
                      >
                        <Box display="flex" alignItems="center" flexWrap="wrap" marginBottom="0">
                          <Typography
                            style={{
                              fontSize: isMobile ? "12px" : "14px",
                              fontWeight: isMobile ? 400 : 700,
                              marginRight: isMobile ? "25px" : undefined,
                              color: theme.palette.grey["800"],
                              flex: 1,
                            }}
                            component="h4"
                          >
                            {t("profile_recommendation_message")}
                          </Typography>
                          <Button
                            aria-controls="clear-button"
                            aria-label={t("clear_choices")}
                            onClick={() => clearChoiceClick()}
                            disabled={selectedTopics.length === 0}
                            className={`${focusClass.focusItem} ${
                              !isMobile ? focusClass.border500 : ""
                            }`}
                            style={
                              isMobile
                                ? {
                                    fontSize: "11px",
                                    fontWeight: "bold",
                                    color: portalSettingsManager.buttonColors?.dark?.active?.text,
                                    backgroundColor:
                                      portalSettingsManager.application?.common?.themeColor,
                                  }
                                : {
                                    fontSize: isMobile ? "11px" : "12px",
                                    fontWeight: isMobile ? "bold" : 400,
                                    color: portalSettingsManager.buttonColors?.dark?.active?.text,
                                    backgroundColor:
                                      portalSettingsManager.application?.common?.themeColor,
                                    textTransform: "none",
                                    borderRadius: 0,
                                    padding: "9px 28px",
                                    lineHeight: "14px",
                                  }
                            }
                          >
                            {t("clear_choices")}
                          </Button>
                        </Box>
                        <ProfileField>
                          <Box display="flex" alignItems="left" flexWrap="wrap" margin="10px 0">
                            {autoEnrolledTopics?.length > 0 ? (
                              <label>{t("admin_selected_topics")}</label>
                            ) : (
                              ""
                            )}
                          </Box>
                        </ProfileField>
                        <Box component="h4">
                          <span
                            style={{
                              fontWeight: isMobile ? 400 : 700,
                              color: theme.palette.grey["800"],
                              fontSize: "14px",
                            }}
                          >
                            {" "}
                            {autoEnrolledTopics
                              .flatMap((item) => (item ? [item.name] : []))
                              .join(", ")}{" "}
                          </span>
                        </Box>
                        <ProfileField>
                          <Box display="flex" alignItems="left" flexWrap="wrap" margin="10px 0">
                            <label>{t("select_topics")}</label>
                          </Box>
                        </ProfileField>
                        <Box
                          style={{
                            display: "grid",
                            gridTemplateColumns: `repeat(${isMobile ? 1 : 3}, minmax(0, 1fr))`,
                            gap: "1rem",
                            marginTop: "30px",
                          }}
                        >
                          {topics.map((topic) =>
                            autoEnrolledTopics.findIndex((x) => x.id == topic.id) == -1 ? (
                              <Toggle
                                key={topic.id}
                                code={topic.id}
                                title={topic.name}
                                isOn={includes(selectedTopics, topic.id)}
                                onChange={toggleTopic}
                                role="button"
                              />
                            ) : (
                              ""
                            ),
                          )}
                        </Box>
                      </TabPanel>
                    )}
                    {userContext?.userContext?.isLineManager && (
                      <TabPanel
                        {...tabPanelSettings}
                        loading={counseleeListLoading}
                        value={currentTabIndex}
                        index={getTabIndexById(ProfileTabId.MyTeam)}
                        style={{ padding: "24px 0" }}
                      >
                        <Box display="flex" justifyContent="space-between">
                          <Box style={{ margin: "10px 0", fontWeight: "bold" }}>
                            {t("expand_members")}
                          </Box>
                          <Box display="block" alignItems="center">
                            <Typography
                              style={{
                                fontSize: "26px",
                                fontWeight: "bold",
                                color: "#333333",
                                lineHeight: "31px",
                                textAlign: "center",
                                margin: "5px 0 5px 0",
                              }}
                            >
                              {counseleesList?.learnersByLineManager?.length}
                            </Typography>
                            <Typography
                              style={{ fontSize: "11px", color: "#333333", lineHeight: "16px" }}
                            >
                              {t("members")}
                            </Typography>
                          </Box>
                        </Box>
                        <Box
                          display="flex"
                          p="0 10px 7px 60px"
                          borderBottom="1px solid #d8d8d8"
                          style={{
                            fontSize: "11px",
                            color: theme.palette.grey["800"],
                            fontWeight: 700,
                            marginLeft: "-14px",
                          }}
                        >
                          <Box flex="1">
                            <span> {t("member_name")}</span>
                          </Box>
                        </Box>
                        {counseleesList?.learnersByLineManager?.map((counselee, ind) => (
                          <CounseleeAccordion
                            expanded={expanded}
                            setExpanded={setExpanded}
                            setLearnerName={setLearnerName}
                            counseleeItems={counseleeItems}
                            sendReminder={(params: any) => {
                              visibleReminder(params, counselee?.id);
                            }}
                            counseleeItemDetail={() => {
                              getCounseleeItemDetail({ variables: { learnerId: counselee?.id } });
                              dispatch({
                                type: "loader/showandhide",
                                payload: { show: true, message: t("profile_loading") },
                              });
                            }}
                            data={counselee}
                            key={ind}
                          />
                        ))}
                        {sendReminder && (
                          <RemindDialog
                            open={sendReminder}
                            learnerName={learnerName}
                            data={result}
                            handleSendReminder={handleSendReminder}
                            remindData={remindData}
                            sendReminder={() => setSendReminder(false)}
                          />
                        )}
                      </TabPanel>
                    )}
                    {showProfileReport && (
                      <TabPanel
                        {...tabPanelSettings}
                        loading={learnerDiagnosticsLoading}
                        value={currentTabIndex}
                        index={getTabIndexById(ProfileTabId.ProfileReport)}
                        style={{ padding: "24px 0" }}
                      >
                        <LearnerReports
                          dataLoading={learnerDiagnosticsLoading}
                          reportsData={learnerDiagnostics}
                          dataLoadFailed={learnerReportsDataFailed}
                          refreshData={refreshReportDiagnositicData}
                        />
                      </TabPanel>
                    )}
                    <TabPanel
                      {...tabPanelSettings}
                      loading={calendarLoading}
                      value={currentTabIndex}
                      index={getTabIndexById(ProfileTabId.MyCalendar)}
                      style={{ padding: "24px 0" }}
                    >
                      <MyCalendar
                        loadData={currentTabIndex == getTabIndexById(ProfileTabId.MyCalendar)}
                        onLoadingDataChanged={setCalendarLoading}
                      />
                    </TabPanel>
                    {showSkillAssessment && (
                      <TabPanel
                        {...tabPanelSettings}
                        loading={mySkillsLoading}
                        value={currentTabIndex}
                        index={getTabIndexById(ProfileTabId.MySkills)}
                        style={{ padding: "24px 0" }}
                      >
                        <MySkills
                          loadData={currentTabIndex == getTabIndexById(ProfileTabId.MySkills)}
                          onLoadingDataChanged={setMySkillsLoading}
                        />
                      </TabPanel>
                    )}
                  </Fragment>
                )}
              </Box>
              {showLearnerMetricsOnProfilePage && (
                <Box
                  borderLeft={`${isMobile ? 0 : 1}px solid ${theme.palette.grey["300"]}`}
                  marginLeft="10px"
                >
                  <MetricsContainer
                    mobile={showMetricsContainer}
                    showMetrics={showMetrics}
                    setShowMetrics={setShowMetrics}
                  >
                    <Typography
                      style={{
                        fontSize: "18px",
                        fontWeight: 700,
                        color: theme.palette.grey["800"],
                        lineHeight: "40px",
                        marginBottom: "10px",
                        textAlign: "center",
                      }}
                      component="h3"
                    >
                      {t("metrics")}
                    </Typography>
                    <Box style={{ borderTop: `1px solid ${theme.palette.grey["300"]}` }} />
                    <Box
                      display="flex"
                      flexDirection={isMobile ? "row" : "column"}
                      alignItems="center"
                      flexWrap="wrap"
                      justifyContent={isMobile ? "space-evenly" : ""}
                    >
                      <D3Chart
                        metricsData={requiredLearning}
                        metricsIcon={requiredIcon}
                        metricsTitle={t("required_learning_metric")}
                      />
                      <D3Chart
                        metricsData={otherLearnings}
                        metricsIcon={otherLearningIcon}
                        metricsTitle={t("metrics_other_learning")}
                      />
                      <D3Chart
                        metricsData={certification}
                        metricsIcon={certificationIcon}
                        metricsTitle={t("metrics_certifications")}
                      />
                    </Box>
                    <Box
                      style={{ margin: "0 -1.5rem 0 -1rem", listStyle: "none" }}
                      display="flex"
                      justifyContent="center"
                      component="ul"
                    >
                      <Legend component="li">{t("enrolled")}</Legend>
                      <Legend
                        component="li"
                        color={portalSettingsManager.statusColors?.inprogress?.back || "#005eb8"}
                      >
                        {t("inprogress")}
                      </Legend>
                      <Legend
                        component="li"
                        color={portalSettingsManager.statusColors?.completed?.back || "#00a3a1"}
                      >
                        {t("completed")}
                      </Legend>
                    </Box>
                  </MetricsContainer>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Container>
      <EmailDialog profile={profile} open={editEmail} handleClose={handleEditEmailClose} />
    </>
  );
};

type LearnerReportsPropType = {
  dataLoading: boolean;
  reportsData: ReportTypes.Reports;
  dataLoadFailed: boolean;
  refreshData: () => void;
};

const getDateForLearnerReports = (date: string) => {
  if (!date) {
    return date;
  }
  const datePart = date.split("T")[0];
  const [year, month, day] = datePart.split("-");
  return `${day} / ${month} / ${year.substring(2)}`;
};

const LearnerReports = ({ reportsData, dataLoadFailed, refreshData }: LearnerReportsPropType) => {
  const reports = reportsData?.diagnosticReports || [];
  const { t } = useTranslation();
  const { openUrl } = openUrlUtil();

  const handleClick = (report: ReportTypes.Reports_diagnosticReports) => {
    const { windowEvent } = openUrl(report.url, report.id);
    const closeWindow = setInterval(function () {
      if (windowEvent?.closed) {
        clearInterval(closeWindow);
        refreshData();
      }
    }, 500);
  };

  const headerText = () => {
    if (dataLoadFailed) {
      return t("report_header_error");
    }

    return reports.length > 0 ? t("report_header_data") : t("report_header_no_data");
  };

  return (
    <>
      <Box style={{ fontWeight: "bold" }}>{headerText()} </Box>

      <Box style={{ borderTop: "1px solid #d8d8d8", marginTop: "20px" }}>
        {reports.map((r, idx) => (
          <div
            key={idx}
            style={{
              minHeight: "50px",
              display: "flex",
              alignItems: "center",
              borderBottom: "1px solid #d8d8d8",
            }}
          >
            <div style={{ width: "50%", paddingLeft: "20px" }}>{r.title}</div>
            <div style={{ width: "20%" }}>{getDateForLearnerReports(r.date)}</div>
            <div style={{ width: "20%" }}>{r.status}</div>
            <div style={{ marginLeft: "auto", marginRight: "10px" }} onClick={() => handleClick(r)}>
              <SvgIcon
                component={arrowIcon}
                style={{
                  marginTop: "20px",
                  color: theme.palette.primary.main,
                  cursor: "pointer",
                }}
              />
            </div>
          </div>
        ))}
      </Box>
    </>
  );
};

const getPages = (itemsPerPage: number, totalItems: number) => {
  const noOfPages = Math.ceil(totalItems / itemsPerPage);
  const pages = [];
  for (let i = 1; i <= noOfPages; i++) {
    pages.push(i);
  }
  return pages;
};

interface MyCalendarProps extends ILazyLoadableComponent {}

const MyCalendar = ({ onLoadingDataChanged, loadData = true }: MyCalendarProps) => {
  const { t } = useTranslation();
  const isMobile = false;
  const [paginationState, setPaginationState] = useState({
    totalCount: 0,
    currentPage: 1,
    itemsPerPage: 10,
  });
  const [sortingState, setSortingState] = useState({
    field: LearnerWorkshopActivitiesSortBy.START_DATE,
    isAscending: true,
  });
  const permissionsLoaded = useSelector(
    (state: RootState) => state.core.userContext?.userContext?.permissionsLoaded,
  );

  const [dataLoadFailed, setDataLoadFailed] = useState(false);
  const [activities, setActivities] = useState<
    (LearnerWorkshops_learnerWorkshopActivities_activities | null | any)[]
  >([]);
  const { totalCount, itemsPerPage, currentPage } = paginationState;
  const pages = getPages(itemsPerPage, totalCount);

  const handleCurrentPageChange = (newPage: number) => {
    setPaginationState({ ...paginationState, currentPage: newPage });
    getCalendarData(
      sortingState.field,
      sortingState.isAscending,
      (newPage - 1) * paginationState.itemsPerPage,
    );
  };

  const handleSortChanged = (name: LearnerWorkshopActivitiesSortBy, isAscending: boolean) => {
    if (name === LearnerWorkshopActivitiesSortBy.VIRTUAL_CLASSROOM_LINK) return false;
    const index = headers.findIndex((h) => h.name == name);
    const newHeaders = [...headers];
    newHeaders[index] = { ...headers[index], isAscending };
    setHeaders(newHeaders);
    setSortingState({ ...sortingState, field: name, isAscending });
    getCalendarData(name, isAscending);
  };

  const [loadMyCalendar, { data: myCalendarData, loading }] =
    useLazyQuery<LearnerWorkshopsTypes.LearnerWorkshops>(GET_MY_WORKSHOPS);

  useEffect(() => {
    if (loadData && !myCalendarData && permissionsLoaded) {
      getCalendarData(sortingState.field, sortingState.isAscending, 0);
    }
  }, [loadData, permissionsLoaded]);

  useEffect(() => {
    if (!loading) {
      let totalCount = 0;

      if (myCalendarData) {
        totalCount = myCalendarData.learnerWorkshopActivities.totalLearnerWorkshopActivitiesCount;
        setActivities(myCalendarData.learnerWorkshopActivities.activities);
      }
      setPaginationState({ ...paginationState, totalCount });
    }
  }, [myCalendarData]);

  useEffect(() => {
    onLoadingDataChanged && onLoadingDataChanged(loading);
  }, [loading]);

  const getCalendarData = (
    sortBy: string | undefined = undefined,
    isAscending = true,
    skip = 0,
  ) => {
    loadMyCalendar({
      variables: {
        sort: { sortBy, isAscending },
        count: Number(process.env.REACT_APP_CATALOG_COUNT),
        skip,
      },
    });
  };

  const [headers, setHeaders] = useState<SortableTableHeaderType[]>([
    {
      name: LearnerWorkshopActivitiesSortBy.PROGRAMME_NAME,
      header: t("course"),
      align: "left",
      isSortable: true,
    },
    {
      name: LearnerWorkshopActivitiesSortBy.ACTIVITY_NAME,
      header: t("activity"),
      align: "left",
      isSortable: true,
    },
    {
      name: LearnerWorkshopActivitiesSortBy.START_DATE,
      header: t("start_date_time"),
      align: "center",
      isAscending: true,
      isSortable: true,
    },
    {
      name: LearnerWorkshopActivitiesSortBy.LOCATION,
      header: t("location"),
      align: "center",
      isSortable: true,
    },
    {
      name: LearnerWorkshopActivitiesSortBy.VIRTUAL_CLASSROOM_LINK,
      header: t("Virtual Classroom Link"),
      align: "center",
      isSortable: true,
    },
  ]);

  const headerText = () => {
    if (dataLoadFailed) {
      return t("my_calendar_header_error");
    }
    if (!loading) {
      return totalCount > 0 ? t("my_calendar") : t("my_calendar_header_no_data");
    }
  };

  return (
    <>
      <Box
        style={{
          margin: "30px 0px 14px 0px",
          fontWeight: 700,
          color: "rgb(51, 51, 51)",
          fontSize: "1.17em",
        }}
        component="h4"
      >
        {headerText()}
      </Box>
      {!dataLoadFailed && totalCount > 0 && (
        <Box>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {headers.map((h) => (
                    <TableCell
                      align="left"
                      style={{ fontWeight: "bold" }}
                      key={h.header}
                      sortDirection={
                        sortingState.field == h.name ? (h.isAscending ? "asc" : "desc") : null
                      }
                    >
                      <SortableTableHeader
                        header={h.header}
                        isAscending={h.isAscending}
                        name={h.name}
                        sortingColumn={sortingState.field}
                        isSortable={h.isSortable}
                        onSortChanged={handleSortChanged}
                      />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody style={{ fontSize: "12px" }}>
                {activities.map((activity, index) => (
                  <TableRow key={index}>
                    <MyCalendarTableCell
                      align="left"
                      text={activity.programmeName}
                      showAsLink={true}
                      link={`/COURSE/${activity.programmeId}`}
                    />
                    <MyCalendarTableCell align="left" text={activity.activityName} />
                    <MyCalendarTableCell align="center" text={activity.startDateTimeDisplay} />
                    <MyCalendarTableCell align="center" text={activity.location} />
                    {activity.location == "Virtual" ? (
                      <MyCalendarTableCell
                        align="center"
                        text={t("Click here to access your virtual classroom")}
                        isExternalLink={true}
                        link={activity.addressLine1}
                        showAsLink={true}
                      />
                    ) : (
                      <MyCalendarTableCell align="center" text={t("Not applicable")} />
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Paginator
            pages={pages}
            currentPage={currentPage}
            isMobile={isMobile}
            onChange={handleCurrentPageChange}
          />
        </Box>
      )}
    </>
  );
};

type MyCalendarTableCellPropsType = {
  align?: "inherit" | "left" | "center" | "right" | "justify";
  text: string;
  showAsLink?: boolean;
  link?: string;
  isExternalLink?: boolean;
};

const MyCalendarTableCell = ({
  align,
  text,
  showAsLink,
  link,
  isExternalLink,
}: MyCalendarTableCellPropsType) => {
  const divRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    if (divRef.current) {
      const { clientWidth, scrollWidth } = divRef.current || { clientWidth: 0, scrollWidth: 0 };
      if (clientWidth < scrollWidth) {
        setShowTooltip(true);
      }
    }
  }, [divRef.current]);

  const getText = () => (
    <Box style={{ display: "flex" }}>
      <div
        style={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
        ref={divRef}
      >
        {text}
      </div>
      {showTooltip && (
        <Tooltip position="bottom" zIndex={10000} title={text} tooltipStyle={{ zIndex: 10000 }}>
          <div
            style={{
              marginLeft: "-20px",
              cursor: "pointer",
              minWidth: "20px",
              minHeight: "20px",
              backgroundColor: "transparent",
            }}
          ></div>
        </Tooltip>
      )}
    </Box>
  );

  const getTextAsLink = () => {
    const style: React.CSSProperties = {
      fontSize: "12px",
      textTransform: "none",
      color: theme.palette.primary.main,
    };
    return isExternalLink ? (
      <a href={link} style={style} target="_blank">
        {getText()}
      </a>
    ) : (
      <Link to={link} style={style}>
        {getText()}
      </Link>
    );
  };

  return <TableCell align={align}>{showAsLink ? getTextAsLink() : getText()}</TableCell>;
};
